import React, { useState, useEffect } from 'react';
import useBrandIcon from '../../hooks/useBrandIcon';
import { ToastPosition, ToastSize, ToastVariant } from '../../utils/types/ui';
import useIcon from "../../hooks/useIcon";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import { useI18n } from '../../i18n';

interface ToastProps {
    message: string;
    onClose: () => void;
    variant?: ToastVariant;
    position?: ToastPosition;
    size?: ToastSize;
}

const Toast: React.FC<ToastProps> = ({ message, onClose, variant = 'info', position = 'bottom-center', size = 'md' }) => {
    const { t } = useI18n();
    const [isVisible, setIsVisible] = useState(true);
    const close = useIcon({icon: faTimes, className: 'text-white dark:text-GREEN', size: 'sm'});
    const notesIcon = useBrandIcon().notes;
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            onClose(); // Close the toast after some time
        }, 5000);

        setTimeoutId(timer);

        return () => clearTimeout(timer);
    }, [onClose]);

    const positionClasses = {
        'top-left': 'top-10 left-4',
        'top-center': 'top-10 left-1/2 transform -translate-x-1/2',
        'top-right': 'top-10 right-4',
        'bottom-left': 'bottom-10 left-4',
        'bottom-center': 'bottom-10 left-1/2 transform -translate-x-1/2',
        'bottom-right': 'bottom-10 right-4',
    };

    const sizeClasses = {
        sm: 'sm:h-10 sm:px-4',
        md: 'sm:h-12 sm:px-6',
        lg: 'sm:h-16 sm:px-8',
        xl: 'sm:h-20 sm:px-10',
    };

    const toastClassName = `fixed ${positionClasses[position]} ${sizeClasses[size]} p-4 text-white rounded-md transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
    }`;

    const variantColors = {
        success: 'bg-ORANGE',
        danger: 'bg-red-500',
        warning: 'bg-yellow-500',
        info: 'bg-orange-500',
    };

    // Add conditional classes for the transition effect when visibility changes
    const transitionClasses = isVisible
        ? 'translate-y-0 ease-in-out'
        : 'translate-y-10 ease-in-out';

    const handleMouseEnter = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };

    const handleMouseLeave = () => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            onClose(); // Close the toast after some time
        }, 3000);

        setTimeoutId(timer);
    };

    return (
        <div
            className={`flex w-4/5 sm:w-auto justify-center items-center z-[9999] ${toastClassName} ${variantColors[variant]} text-white cursor-pointer transform ${transitionClasses} ease-in-out`}
            onClick={onClose}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="flex items-center justify-between transition-all delay-1000 ease-linear">
                <div className="flex justify-between items-center">
                    <img src={notesIcon} alt={t('components.toast.musicAlt')} className="w-4 h-4 sm:w-6 sm:h-6 mr-2"/>
                    <p className={'text-xs lg:text-lg capitalize'}>{message}</p>
                </div>
                <button className={'ml-4 self-start'} onClick={onClose}>
                    {close}
                </button>
            </div>
        </div>
    );
};

export default Toast;
